import { Bird } from '../api/birds';

/**
 * Get the display ID for a bird
 * Shows band ID if available and not a temporary chick ID, otherwise shows bird record ID
 */
export const getBirdDisplayId = (bird: Bird | { id: string; bandId: string } | null | undefined): string => {
  if (!bird) return 'Unknown';
  
  if (bird.bandId && !bird.bandId.startsWith('CHICK_')) {
    return bird.bandId;
  }
  return bird.id;
};

/**
 * Check if a bird is an unbanded chick (has temporary CHICK_ band ID)
 */
export const isUnbandedChick = (bird: Bird | { id: string; bandId: string } | null | undefined): boolean => {
  if (!bird) return false;
  return bird.bandId && bird.bandId.startsWith('CHICK_');
};

/**
 * Get a full display name for a bird including species
 */
export const getBirdDisplayName = (bird: Bird | { id: string; bandId: string; species?: { commonName: string } } | null | undefined): string => {
  if (!bird) return 'Unknown';
  
  const displayId = getBirdDisplayId(bird);
  const speciesName = 'species' in bird && bird.species?.commonName || 'Unknown';
  return `${displayId} (${speciesName})`;
};

/**
 * Get a short display name for a bird (just the ID)
 */
export const getBirdShortDisplayName = (bird: Bird | { id: string; bandId: string } | null | undefined): string => {
  return getBirdDisplayId(bird);
};

/**
 * Get display information for a bird with metadata
 */
export const getBirdDisplayInfo = (bird: Bird | { id: string; bandId: string; species?: { commonName: string } } | null | undefined) => {
  if (!bird) {
    return {
      displayId: 'Unknown',
      isUnbanded: false,
      speciesName: 'Unknown',
      fullName: 'Unknown',
      tooltip: 'Unknown'
    };
  }
  
  const displayId = getBirdDisplayId(bird);
  const isUnbanded = isUnbandedChick(bird);
  const speciesName = 'species' in bird && bird.species?.commonName || 'Unknown';
  
  return {
    displayId,
    isUnbanded,
    speciesName,
    fullName: `${displayId} (${speciesName})`,
    tooltip: isUnbanded ? `Unbanded chick - Bird ID: ${bird.id}` : `${displayId} (${speciesName})`
  };
}; 